import React from "react";
import { Link } from "react-router-dom";
import { PageHeader, GedPrepDisclaimer } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowLeft, ExternalLink, Calendar, CreditCard, Monitor, MapPin, IdCard, Check,
  Calculator, BookText, FlaskConical, Landmark, ShieldCheck, Clock, Target, GraduationCap, Info,
} from "lucide-react";

const STEPS = [
  { icon: IdCard, title: "Confirm you're eligible", body: "You must be at least 16, not currently enrolled in high school, and not already a high-school graduate. Age and residency rules can vary — check your state's requirements on ged.com before you register." },
  { icon: Calendar, title: "Create your account at ged.com", body: "Set up a free account at ged.com (that's the official site run by GED Testing Service). You'll use the same login to schedule tests and view scores." },
  { icon: Monitor, title: "Pick online or in-person", body: "Online proctored tests are taken from home on a laptop with a webcam. In-person tests are taken at an approved Pearson VUE test center. Not every subject is offered online in every state." },
  { icon: Calculator, title: "Schedule one subject at a time", body: "The GED is four separate subject tests — Math, RLA, Science and Social Studies. You can take them in any order and on different days. We recommend taking the one you've scored highest on in practice first to build momentum." },
  { icon: CreditCard, title: "Pay the exam fee", body: "Fees are set per state and typically range from $36 to $44 per subject (and sometimes bundled). Some states subsidise the test for residents. The amount owed shows at scheduling." },
  { icon: ShieldCheck, title: "Test day: bring your ID", body: "You'll need a government-issued photo ID with a signature. For online testing you'll do a short room scan with the proctor. For in-person, arrive 15 minutes early." },
  { icon: Target, title: "Read your scores", body: "Scores post to your ged.com account within a few hours (Math/Science/Social) or up to three business days (RLA, because of the essay). 145 passes a subject, 165 is 'College Ready', and 175 is 'College Ready + Credit'." },
];

const SUBJECTS = [
  { icon: Calculator, name: "Mathematical Reasoning", time: "115 minutes", notes: "On-screen calculator for part of the test. Expect algebra, functions, and data interpretation." },
  { icon: BookText, name: "Reasoning Through Language Arts", time: "150 minutes", notes: "Reading, grammar, and a 45-minute extended-response essay from two passages." },
  { icon: FlaskConical, name: "Science", time: "90 minutes", notes: "Life, physical, and earth-and-space science questions, often with a graph or short passage." },
  { icon: Landmark, name: "Social Studies", time: "70 minutes", notes: "Civics, US history, economics, and geography — frequently with a quote, map, or chart." },
];

const DO_BRING = [
  "A government-issued photo ID with your signature",
  "Your ged.com login (and your test confirmation email)",
  "A quiet room with a desk and good light (online testing)",
  "A working webcam and mic (online testing)",
];
const DONT_BRING = [
  "Phones, smart watches, or any second screen in the room",
  "Notes, textbooks or printed material",
  "Hats, hoods, or sunglasses during testing",
  "Food — a water bottle without a label is usually OK",
];

const RECOMMENDED_SCORE = 145;

export default function RealGedHandoff() {
  return (
    <div className="max-w-[1100px]">
      <Button variant="ghost" asChild className="mb-4 -ml-3" data-testid="real-ged-back">
        <Link to="/ged"><ArrowLeft className="h-4 w-4 mr-1" />Back to GED Prep</Link>
      </Button>

      <PageHeader
        eyebrow="Taking the official GED"
        title="Ready to take the real GED?"
        subtitle="You've put in the work here. The official GED exam is run by GED Testing Service through ged.com — here's exactly how to register and what to expect on test day."
      />

      <GedPrepDisclaimer className="mb-6" />

      <div className="rounded-[20px] border border-[#1F9D55]/25 bg-[#DDF7E7]/40 p-6 sm:p-8 mb-8" data-testid="real-ged-cta-top">
        <div className="flex flex-wrap items-start gap-6">
          <div className="h-14 w-14 rounded-2xl bg-[#DDF7E7] text-[#14663A] flex items-center justify-center shrink-0"><GraduationCap className="h-7 w-7" /></div>
          <div className="flex-1 min-w-[240px]">
            <div className="font-display font-semibold text-2xl text-[#071A22]">Register at ged.com</div>
            <p className="mt-2 text-[15px] text-muted-foreground max-w-xl">
              Only GED Testing Service can give you an official GED credential. Create a free account, pick your state, and schedule the subjects you want to take.
            </p>
          </div>
          <Button asChild className="h-11" data-testid="real-ged-primary-cta">
            <a href="https://ged.com" target="_blank" rel="noreferrer">
              Go to ged.com<ExternalLink className="h-4 w-4 ml-2" />
            </a>
          </Button>
        </div>
      </div>

      <section className="mb-10">
        <h2 className="text-2xl font-display font-semibold mb-5">Step-by-step</h2>
        <ol className="space-y-4" data-testid="real-ged-steps">
          {STEPS.map((s, i) => (
            <li key={s.title} className="bg-white border border-border rounded-[14px] p-5 flex gap-4" data-testid={`real-ged-step-${i + 1}`}>
              <div className="h-10 w-10 rounded-full bg-[#F5F1E6] text-[#071A22] font-display font-semibold flex items-center justify-center shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 font-semibold text-[#071A22]"><s.icon className="h-4 w-4 text-[#0B6B6B]" />{s.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-display font-semibold mb-5">The four subject tests</h2>
        <div className="grid md:grid-cols-2 gap-4" data-testid="real-ged-subjects">
          {SUBJECTS.map((s) => (
            <div key={s.name} className="bg-white border border-border rounded-[14px] p-5" data-testid={`real-ged-subject-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#D7F3F1] text-[#0B6B6B] flex items-center justify-center"><s.icon className="h-5 w-5" /></div>
                <div className="font-display font-semibold">{s.name}</div>
              </div>
              <div className="mt-3 text-sm text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{s.time}</div>
              <p className="mt-2 text-sm">{s.notes}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl bg-[#F5F1E6] border border-border p-4 text-sm flex gap-2 text-[#123645]" data-testid="real-ged-scoring">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-[#0F7B7D]" />
          <div><span className="font-semibold">Scoring: </span>Each subject is scored 100–200. You need <span className="font-semibold">{RECOMMENDED_SCORE}</span> to pass each one. 165 is "College Ready" and 175 earns up to 3 college credits per subject.</div>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-display font-semibold mb-5">Test-day checklist</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-border rounded-[14px] p-5" data-testid="real-ged-bring">
            <div className="flex items-center gap-2 font-semibold text-[#14663A]"><Check className="h-4 w-4" />Bring these</div>
            <ul className="mt-3 space-y-2 text-sm">
              {DO_BRING.map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-[#1F9D55] mt-0.5 shrink-0" />{x}</li>)}
            </ul>
          </div>
          <div className="bg-white border border-border rounded-[14px] p-5" data-testid="real-ged-dont-bring">
            <div className="flex items-center gap-2 font-semibold text-[#9B2C2C]">Leave these behind</div>
            <ul className="mt-3 space-y-2 text-sm">
              {DONT_BRING.map((x) => <li key={x} className="flex gap-2 text-muted-foreground">· {x}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="mb-10" data-testid="real-ged-faq">
        <h2 className="text-2xl font-display font-semibold mb-5">Frequently asked</h2>
        <Accordion type="single" collapsible className="bg-white border border-border rounded-[14px]">
          <AccordionItem value="age">
            <AccordionTrigger className="px-5" data-testid="faq-age">Am I old enough to take the GED?</AccordionTrigger>
            <AccordionContent className="px-5 text-sm text-muted-foreground">You must be at least 16 in most states, and under 18 usually requires parental consent and an age/withdrawal letter. Each state's rules are listed on ged.com when you select your location.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="cost">
            <AccordionTrigger className="px-5" data-testid="faq-cost">How much does the real test cost?</AccordionTrigger>
            <AccordionContent className="px-5 text-sm text-muted-foreground">Typical US pricing is $36–$44 per subject test, so about $144–$176 for all four. Some states offer a reduced bundle or state-subsidised testing — the final price shows when you schedule.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="online">
            <AccordionTrigger className="px-5" data-testid="faq-online">Can I take it online from home?</AccordionTrigger>
            <AccordionContent className="px-5 text-sm text-muted-foreground">In most US states, yes — using a webcam, mic, and a browser-based proctor. GED Testing Service will check your webcam equipment and your room before each test. Some states still require in-person testing for one or more subjects.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="retake">
            <AccordionTrigger className="px-5" data-testid="faq-retake">What if I don't pass a subject?</AccordionTrigger>
            <AccordionContent className="px-5 text-sm text-muted-foreground">You can retake a subject up to two more times without waiting. After three attempts in a year, there's a 60-day waiting period. Most states charge a discounted retake fee for the first two retakes.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="scores">
            <AccordionTrigger className="px-5" data-testid="faq-scores">How soon do I get my official score?</AccordionTrigger>
            <AccordionContent className="px-5 text-sm text-muted-foreground">Math, Science, and Social Studies usually post within a few hours. RLA takes up to three business days because the essay is hand-scored. You can see all your official scores in your ged.com account.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="scholar">
            <AccordionTrigger className="px-5" data-testid="faq-scholar">Does my Scholar Campus practice score count?</AccordionTrigger>
            <AccordionContent className="px-5 text-sm text-muted-foreground">No — practice-test estimates here are for preparation only. Only tests you book and sit through ged.com produce an official GED score and credential.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="location">
            <AccordionTrigger className="px-5" data-testid="faq-location">Where do I take it in person?</AccordionTrigger>
            <AccordionContent className="px-5 text-sm text-muted-foreground">In-person testing happens at a Pearson VUE test center (that's who runs GED test centers). When you schedule on ged.com you can search by ZIP code.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <div className="rounded-[20px] border border-border bg-white p-6 sm:p-8 text-center" data-testid="real-ged-cta-bottom">
        <h2 className="font-display font-semibold text-2xl">You've got this.</h2>
        <p className="mt-2 text-muted-foreground max-w-xl mx-auto">Head to ged.com, pick a subject, and lock in your date. We'll keep your prep and practice tests here as long as you need them.</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Button asChild className="h-11" data-testid="real-ged-bottom-cta">
            <a href="https://ged.com/schedule" target="_blank" rel="noreferrer">
              Schedule on ged.com<ExternalLink className="h-4 w-4 ml-2" />
            </a>
          </Button>
          <Button asChild variant="outline" className="bg-white h-11" data-testid="real-ged-locator-link">
            <a href="https://ged.com/locate-a-testing-center" target="_blank" rel="noreferrer">
              <MapPin className="h-4 w-4 mr-2" />Find a test center
            </a>
          </Button>
          <Button asChild variant="ghost" className="h-11" data-testid="real-ged-back-bottom">
            <Link to="/ged">Keep practicing</Link>
          </Button>
        </div>
      </div>

      <p className="mt-6 text-xs text-muted-foreground max-w-3xl">
        GED® is a registered trademark of the American Council on Education. Scholar Campus is not affiliated with and is not endorsed by GED Testing Service. Official registration, scheduling, scoring and credentials happen only through ged.com.
      </p>
    </div>
  );
}
